import React from 'react';
import { SoccerScore, SoccerEvent } from '../../types';

interface Props { score: SoccerScore; homeTeam: string; awayTeam: string; timerMs?: number; }

const STATUS_META: Record<string, { label: string; color: string; dot?: boolean }> = {
  not_started: { label: 'Pre-Game',    color: '#4A5568' },
  first_half:  { label: '1st Half',    color: '#F97316', dot: true },
  half_time:   { label: 'Half Time',   color: '#EAB308' },
  second_half: { label: '2nd Half',    color: '#F97316', dot: true },
  full_time:   { label: 'Full Time',   color: '#22C55E' },
  extra_time:  { label: 'ET 1st Half', color: '#EC4899', dot: true },
  extra_time_half:   { label: 'ET Half Time',  color: '#EAB308' },
  extra_time_second: { label: 'ET 2nd Half',   color: '#EC4899', dot: true },
  extra_time_full:   { label: 'ET Full Time',  color: '#22C55E' },
  penalties:   { label: 'Penalty Kicks', color: '#A78BFA' },
};

const EVENT_ICONS: Record<string, string> = {
  goal: '⚽', assist: '🎯', yellow_card: '🟨', red_card: '🟥', substitution: '🔄',
};
const EVENT_LABELS: Record<string, string> = {
  goal: 'Goal', assist: 'Assist', yellow_card: 'Yellow Card',
  red_card: 'Red Card', substitution: 'Substitution',
};

function fmtTimer(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

const SoccerBoxScore: React.FC<Props> = ({ score, homeTeam, awayTeam, timerMs }) => {
  const meta = STATUS_META[score.status] ?? { label: score.status, color: '#8892A4' };
  const isActive = ['first_half', 'second_half', 'extra_time', 'extra_time_second'].includes(score.status);
  const homeGoals = (score.events ?? []).filter(e => e.team === 'home' && e.event_type === 'goal');
  const awayGoals = (score.events ?? []).filter(e => e.team === 'away' && e.event_type === 'goal');
  const allEvents = score.events ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

      {/* ── Main scoreboard ─────────────────────────────────────────────────── */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '14px',
        overflow: 'hidden',
      }}>
        {/* Status bar */}
        <div style={{
          background: `${meta.color}14`, borderBottom: '1px solid var(--hairline)',
          padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}>
          {meta.dot && isActive && (
            <span style={{
              width: 8, height: 8, borderRadius: '50%', background: meta.color,
              display: 'inline-block', animation: 'pulseDot 1.2s ease-in-out infinite',
            }} />
          )}
          <span style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '1px', color: meta.color, textTransform: 'uppercase' }}>
            {meta.label}
          </span>
          {isActive && timerMs !== undefined && timerMs > 0 && (
            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink-2)', fontFamily: 'monospace' }}>
              {fmtTimer(timerMs)}
            </span>
          )}
        </div>

        {/* Score panel */}
        <div style={{ display: 'flex', alignItems: 'stretch' }}>

          {/* Away */}
          <div style={{ flex: 1, padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px' }}>Away</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--ink-1)', textAlign: 'right', lineHeight: 1.2 }}>{awayTeam}</div>
            <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end' }}>
              {awayGoals.map((e, i) => (
                <div key={i} style={{ fontSize: '11px', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>{e.player_name || awayTeam}</span>
                  <span style={{ color: '#F97316', fontWeight: 700 }}>{e.minute}'</span>
                  <span>⚽</span>
                </div>
              ))}
            </div>
          </div>

          {/* Centre scores */}
          <div style={{
            padding: '20px 12px', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '0',
            borderLeft: '1px solid var(--hairline)', borderRight: '1px solid var(--hairline)',
            minWidth: '130px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '60px', fontWeight: 900, lineHeight: 1, color: 'var(--ink-1)', letterSpacing: '-2px' }}>
                {score.away_score}
              </span>
              <span style={{ fontSize: '24px', color: 'var(--hairline)', fontWeight: 700, lineHeight: 1 }}>–</span>
              <span style={{ fontSize: '60px', fontWeight: 900, lineHeight: 1, color: 'var(--ink-1)', letterSpacing: '-2px' }}>
                {score.home_score}
              </span>
            </div>
          </div>

          {/* Home */}
          <div style={{ flex: 1, padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px' }}>Home</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--ink-1)', lineHeight: 1.2 }}>{homeTeam}</div>
            <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {homeGoals.map((e, i) => (
                <div key={i} style={{ fontSize: '11px', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>⚽</span>
                  <span style={{ color: '#F97316', fontWeight: 700 }}>{e.minute}'</span>
                  <span>{e.player_name || homeTeam}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Events timeline ──────────────────────────────────────────────────── */}
      {allEvents.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '14px', overflow: 'hidden' }}>
          <div style={{
            padding: '10px 16px', borderBottom: '1px solid var(--hairline)',
            fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px',
          }}>
            Match Events
          </div>
          <div style={{ position: 'relative', padding: '8px 0' }}>
            {/* Centre timeline line */}
            <div style={{
              position: 'absolute', left: '50%', top: 0, bottom: 0,
              width: 1, background: 'var(--hairline)', transform: 'translateX(-50%)',
            }} />

            {allEvents.map((event: SoccerEvent, i: number) => {
              const isHome = event.team === 'home';
              const icon   = EVENT_ICONS[event.event_type] ?? '●';
              const label  = EVENT_LABELS[event.event_type] ?? event.event_type;
              const evColor = event.event_type === 'goal'        ? (isHome ? '#22C55E' : '#EF4444')
                            : event.event_type === 'assist'      ? '#F97316'
                            : event.event_type === 'yellow_card' ? '#EAB308'
                            : event.event_type === 'red_card'    ? '#EF4444'
                            : '#4A5568';

              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', padding: '7px 16px',
                  justifyContent: isHome ? 'flex-end' : 'flex-start',
                }}>
                  {/* Away side (left) */}
                  {!isHome && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      background: `${evColor}10`, border: `1px solid ${evColor}30`,
                      borderRadius: '8px', padding: '6px 10px', maxWidth: '45%',
                    }}>
                      <span style={{ fontSize: '16px', lineHeight: 1 }}>{icon}</span>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 800, color: evColor }}>{event.minute}' — {label}</div>
                        <div style={{ fontSize: '12px', color: 'var(--ink-1)', fontWeight: 600 }}>{event.player_name || awayTeam}</div>
                      </div>
                    </div>
                  )}

                  {/* Centre minute pip */}
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: `${evColor}22`, border: `2px solid ${evColor}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, margin: '0 8px', fontSize: '9px', fontWeight: 800, color: evColor,
                    zIndex: 1,
                  }}>
                    {event.minute}'
                  </div>

                  {/* Home side (right) */}
                  {isHome && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'row-reverse',
                      background: `${evColor}10`, border: `1px solid ${evColor}30`,
                      borderRadius: '8px', padding: '6px 10px', maxWidth: '45%',
                    }}>
                      <span style={{ fontSize: '16px', lineHeight: 1 }}>{icon}</span>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', fontWeight: 800, color: evColor }}>{event.minute}' — {label}</div>
                        <div style={{ fontSize: '12px', color: '#F0F6FC', fontWeight: 600 }}>{event.player_name || homeTeam}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
};

export default SoccerBoxScore;
